import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, Input } from '@angular/core';
import { Reward } from 'src/interfaces/reward.interface';
import { RewardsBalances } from 'src/interfaces/rewards-balances.interface';
import { TextField } from 'src/vendors/directus/interfaces/text-field.interface';
import { VendorSetup } from 'src/vendors/directus/interfaces/vendor.interface';
import { User } from 'src/interfaces/user.interface';
import { Select, Store } from '@ngxs/store';
import { Observable, of, Subscription, combineLatest } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { GlobalStateModel } from '../../../../store/state.model';
import { Router } from '@angular/router';
import { NavigationService } from '@modules/navigation/services';
import { LoyaltySection } from '../../../../vendors/directus/interfaces/main-settings.interface';
import { LoyaltyReward } from 'src/interfaces/loyalty-reward.interface';
import { SegmentCustomEvent } from '@ionic/angular';
import { LoyaltyService } from 'src/services/vendor-config-service/loyalty.service';

interface SegmentedRewards {
  allHavePointCost: boolean;
  rewards: Reward[];
  unlockables: Reward[];
  rawRewards: Reward[];
  hasUnlockables: boolean;
}

@Component({
  selector: 'app-rewards-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './rewards-section.component.html',
  styleUrls: ['./rewards-section.component.scss'],
})
export class RewardsSectionComponent implements OnInit, OnDestroy {
  @Select((state: GlobalStateModel) => state.user.rewards) rewards$: Observable<Reward[]>;
  @Select((state: GlobalStateModel) => state.user.loyaltyRewards) loyaltyRewards$: Observable<LoyaltyReward[]>;
  @Select((state: GlobalStateModel) => state.user.rewardPoints) rewardPoints$: Observable<RewardsBalances>;

  @Output() redeemReward = new EventEmitter<Reward>();
  @Output() redeemInStoreReward = new EventEmitter<Reward>();
  @Output() removeReward = new EventEmitter<Reward>();
  @Output() openRewardsBalances = new EventEmitter<void>();
  @Output() orderNow = new EventEmitter<void>();

  @Input() currencyCode: string;
  @Input() textFields: TextField;
  @Input() displayBalances: RewardsBalances;
  @Input() stagedReward: Reward;
  @Input() displayVendorSetup: VendorSetup;
  @Input() displayUser: User;
  @Input() isDashboard = false;
  @Input() section: LoyaltySection;

  rewardsRoute: string;
  locationsRoute: string;

  displayRewards$: Observable<Reward[]>;
  segmentedRewards$: Observable<SegmentedRewards>;
  selectedSegment: 'rewards' | 'unlockables' = 'rewards';
  error = false;

  private subs: Subscription[] = [];

  constructor(
    private router: Router,
    private navigation: NavigationService,
    private store: Store,
    private loyaltyService: LoyaltyService
  ) {}

  ngOnInit() {
    this.rewardsRoute = this.navigation.getRewardsPageSlug();
    this.locationsRoute = this.navigation.getLocationsPageSlug();

    // Setup segmented rewards
    this.segmentedRewards$ = combineLatest([this.rewards$, this.loyaltyRewards$, this.rewardPoints$]).pipe(
      switchMap(([rewards, loyaltyRewards]) => {
        const safeRewards = rewards || [];
        const safeLoyaltyRewards = loyaltyRewards || [];

        // Get redeemables from loyalty service if available
        return this.getRedeemables().pipe(
          map(redeemables => {
            const mappedRewards = safeRewards.map(reward => {
              const matchingLoyalty = safeLoyaltyRewards.find(lr => lr.externalReferenceID === String(reward.rewardID));
              return {
                ...reward,
                pointCost: matchingLoyalty ? matchingLoyalty.pointCost : null,
                description: matchingLoyalty ? matchingLoyalty.description : reward.description,
              };
            });

            // Free rewards: pointCost is null or 0
            const freeRewards = mappedRewards.filter(r => r.pointCost === null || r.pointCost === 0);
            // Store rewards: pointCost greater than 0, sorted ascending
            const storeRewards = mappedRewards.filter(r => r.pointCost > 0).sort((a, b) => a.pointCost - b.pointCost);

            // Deduplicate unlockables by rewardID
            const storeRewardIDs = new Set(storeRewards.map(r => String(r.rewardID)));
            const uniqueRedeemables = redeemables.filter(r => !storeRewardIDs.has(String(r.rewardID)));
            const allUnlockables = [...storeRewards, ...uniqueRedeemables];

            return {
              allHavePointCost: mappedRewards.every(r => r.pointCost !== null && r.pointCost !== undefined),
              rewards: freeRewards,
              unlockables: allUnlockables,
              rawRewards: mappedRewards,
              hasUnlockables: allUnlockables.length > 0,
            };
          })
        );
      })
    );

    this.displayRewards$ = this.rewards$.pipe(
      filter(rewards => !!rewards),
      switchMap(rewards => {
        if (rewards && rewards.length > 0) {
          return of(rewards);
        } else {
          return of([]);
        }
      }),
      catchError(() => {
        this.error = true;
        return of([]);
      })
    );
  }

  // Helper method to get redeemables from loyalty service
  private getRedeemables(): Observable<Reward[]> {
    return this.loyaltyService.getService().pipe(
      switchMap(lService => {
        if (lService && lService.getRedeemables) {
          return lService.getRedeemables().pipe(
            map(redeemables => {
              if (redeemables && redeemables.length > 0) {
                return redeemables;
              }
              return [] as Reward[];
            }),
            catchError(() => of([] as Reward[]))
          );
        }
        return of([] as Reward[]);
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  rewardTrackBy(index: number, item: Reward) {
    return item.rewardID;
  }

  onSegmentChange(event: SegmentCustomEvent) {
    this.selectedSegment = event.detail.value as 'rewards' | 'unlockables';
  }
}
